'use client'

import AutomationTemplate from '@/components/DashboardV2/AutomationTemplate'
import { ListPageTemplate } from '@/components/ListPageTemplate/ListPageTemplate'
import { useGetUserQuery } from '@/lib/features/user/userApi'
import {
  useDeleteWorkflowMutation,
  useGetWorkspaceTagsQuery,
  useGetWorkspaceWorkflowMetadataQuery,
  useSetWorkflowBookmarkMutation,
  useUpdateWorkflowMutation,
} from '@/lib/features/workflow/workflowApi'
import { PAGE_PATH } from '@/lib/page-paths'
import { formatWorkflowName } from '@/utils/workflowUtils'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { ApiWorkflowMetadata } from 'sprites-types'

const PAGE_SIZE = 20

export default function AutomationsClient() {
  const [offset, setOffset] = useState(0)
  const [allLoadedWorkflows, setAllLoadedWorkflows] = useState<ApiWorkflowMetadata[]>([])
  const [tagDropdownState, setTagDropdownState] = useState<{
    workflowId: string | null
    position: { x: number; y: number } | null
  }>({
    workflowId: null,
    position: null,
  })
  const router = useRouter()

  const { data: userData } = useGetUserQuery()
  const workspaceId = userData?.workspace?.id

  const { data: workspaceTags = [] } = useGetWorkspaceTagsQuery(workspaceId, {
    skip: !workspaceId,
  })

  const {
    data: workflowData,
    isError: isWorkflowsError,
    isLoading: isLoadingWorkflows,
    isFetching: isFetchingWorkflows,
  } = useGetWorkspaceWorkflowMetadataQuery({ limit: PAGE_SIZE, offset })

  // Update workflows when data arrives (both initial load and pagination)
  useEffect(() => {
    if (!workflowData?.workflows) return

    if (offset === 0) {
      // Initial load or filter reset - replace all workflows
      setAllLoadedWorkflows(workflowData.workflows)
    } else {
      // Pagination - merge new workflows
      setAllLoadedWorkflows((prev) => {
        const workflowMap = new Map(prev.map((w) => [w.id, w]))
        workflowData.workflows.forEach((workflow) => {
          workflowMap.set(workflow.id, workflow)
        })
        return Array.from(workflowMap.values())
      })
    }
  }, [workflowData?.workflows, offset])

  const [deleteWorkflow] = useDeleteWorkflowMutation()
  const [updateWorkflow] = useUpdateWorkflowMutation()
  const [setWorkflowBookmark] = useSetWorkflowBookmarkMutation()

  const handleDuplicateWorkflow = useCallback(
    (workflowId: string) => {
      return () => {
        router.push(`${PAGE_PATH.COPILOT_TEMPLATE}/${workflowId}`)
      }
    },
    [router]
  )

  const handleDeleteWorkflow = useCallback(
    (workflowId: string) => async () => {
      try {
        await deleteWorkflow(workflowId).unwrap()
        toast.success('Workflow deleted successfully')
      } catch (error) {
        console.error('Failed to delete workflow:', error)
        toast.error('Failed to delete workflow. Please try again.')
      }
    },
    [deleteWorkflow]
  )

  const handleRenameWorkflow = useCallback(
    (workflowId: string) => {
      return async (newName: string) => {
        try {
          await updateWorkflow({
            workflowRef: workflowId,
            config: { name: newName },
          }).unwrap()
          toast.success('Automation renamed successfully')
        } catch (error: any) {
          console.error('Failed to rename workflow:', error)
          const errorMessage =
            error?.data?.error || error?.error || 'Failed to rename automation. Please try again.'
          toast.error(errorMessage)
        }
      }
    },
    [updateWorkflow]
  )

  const handleLoadMore = useCallback(() => {
    setOffset((prev) => prev + PAGE_SIZE)
  }, [])

  return (
    <ListPageTemplate
      title="Automations"
      items={allLoadedWorkflows}
      isLoading={isLoadingWorkflows}
      isError={isWorkflowsError}
      isFetching={isFetchingWorkflows}
      pagination={workflowData?.pagination}
      onLoadMore={handleLoadMore}
      availableTags={workspaceTags}
      getItemName={(workflow) => formatWorkflowName(workflow.name)}
      getItemDescription={(workflow) => workflow.description || ''}
      emptyStateMessage="No automations yet"
      emptyStateSubMessage="Automations you create will appear here"
      errorMessage="Failed to load automations. Please try again later."
      _itemTypeSingular="automation"
      itemTypePlural="automations"
      filterVariant="favorite"
      renderItem={(workflow) => (
        <AutomationTemplate
          id={workflow.id}
          title={formatWorkflowName(workflow.name)}
          description={workflow.description || ''}
          updatedAt={workflow.updatedAt}
          href={`${PAGE_PATH.WORKFLOW}/${workflow.id}`}
          onDuplicateAction={handleDuplicateWorkflow(workflow.id)}
          onDeleteAction={handleDeleteWorkflow(workflow.id)}
          onRenameAction={handleRenameWorkflow(workflow.id)}
          isBookmarked={workflow.isBookmarked}
          onBookmarkToggle={async () => {
            const newBookmarkState = !workflow.isBookmarked
            // Update local state immediately for instant feedback
            setAllLoadedWorkflows((prev) =>
              prev.map((w) => (w.id === workflow.id ? { ...w, isBookmarked: newBookmarkState } : w))
            )
            try {
              await setWorkflowBookmark({
                workflowId: workflow.id,
                bookmarked: newBookmarkState,
              }).unwrap()
            } catch (error) {
              // Revert on error
              setAllLoadedWorkflows((prev) =>
                prev.map((w) =>
                  w.id === workflow.id ? { ...w, isBookmarked: !newBookmarkState } : w
                )
              )
              throw error
            }
          }}
          tags={workflow.tags}
          onEditTags={(e: React.MouseEvent) => {
            setTagDropdownState({
              workflowId: workflow.id,
              position: { x: e.clientX, y: e.clientY },
            })
          }}
          isTagDropdownOpen={tagDropdownState.workflowId === workflow.id}
          workspaceId={workflow.workspaceId}
          onCloseTagDropdown={() => {
            setTagDropdownState({ workflowId: null, position: null })
          }}
          dropdownPosition={
            tagDropdownState.workflowId === workflow.id ? tagDropdownState.position : null
          }
          requiredIntegrations={workflow.requiredIntegrations}
        />
      )}
    />
  )
}
